import {Component, effect, inject, OnInit, Signal} from '@angular/core';
import {FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators} from '@angular/forms';
import {Store} from '@ngrx/store';
import {SharedModalUtils} from '../../../../shared/shared-utils/shared-modal/shared-modal.utils';
import {IconsEnum} from '../../../../shared-modules/shared-icons/models/icons.enum';
import {IconColourEnum} from '../../../../shared-modules/shared-icons/models/icons-colour.enum';
import {_DEFAULT_APPLICATION_TOOLTIPS} from '../../../core/models';
import {IStockItemEditThresholds, SettingsReferenceEnum} from '../../models';
import {selectSelectedSetting, selectSelectedStoreStockThreshold} from '../../store/setting.selectors';
import {IUser} from '../../../user-access/models';
import {selectUser} from '../../../user-access/user-store/user.selectors';
import {IStore} from '../../../../shared/shared-models/store/store';
import {selectSelectedStore} from '../../../user-access/user-stores-store/user-store.selectors';
import {getFieldErrorString} from '../../../../shared/utils/form/form.utils';
import {settingsSetSelected, settingStoreStockEditThresholdRules} from '../../store/setting.actions';
import {ISharedModalBasic} from '../../../../shared/shared-models/modals/shared-modal-basic';
import {IModalDismissResult} from '../../../../shared/shared-services/overlay/modal-controller.service';
import {ICrudOptions, IUserAccessFeatureStockRules} from '../../../user-access/models/user-access-features/access';
import {
  selectUserAccessFeatureStockRulesForSelectedStore,
} from '../../../user-access/user-access-store/user-access.selectors';

@Component({
  selector: 'app-feature-stock-price-threshold',
  templateUrl: './feature-stock-price-threshold.component.html',
  styleUrl: './feature-stock-price-threshold.component.scss',
  standalone: false,
})
export class FeatureStockPriceThresholdComponent implements OnInit {

  //#region INJECTORS
  private formBuilder = inject(FormBuilder);
  private _store = inject(Store);
  private modalUtils = inject(SharedModalUtils);
  //#endregion

  //#region PROPERTIES
  formStockThreshold: FormGroup;

  protected readonly icons = IconsEnum;
  protected readonly iconColours = IconColourEnum;
  protected readonly tooltips = _DEFAULT_APPLICATION_TOOLTIPS;

  private readonly _settingChanged: Signal<SettingsReferenceEnum> = this._store.selectSignal(selectSelectedSetting);
  private readonly _stockThreshold: Signal<IStockItemEditThresholds> = this._store.selectSignal(selectSelectedStoreStockThreshold);
  private readonly _user: Signal<IUser> = this._store.selectSignal(selectUser);
  private readonly _selectedStore: Signal<IStore> = this._store.selectSignal(selectSelectedStore);
  private readonly _uaStockRules: Signal<IUserAccessFeatureStockRules> = this._store.selectSignal(selectUserAccessFeatureStockRulesForSelectedStore);

  //#endregion

  //#region INITIALIZATION
  constructor() {
    effect(() => {
      const userAccess = this._uaStockRules();
      const stockThreshold = this._stockThreshold();
      this.initializeForms(userAccess, stockThreshold);
    });
    effect(() => {
      const ref = this._settingChanged();
      this._store.dispatch(settingsSetSelected.setSelectedReferenceForNavigation({ref}));
    });
  }

  //#endregion

  //#region ACCESSOR - STOCK THRESHOLD SETTINGS

  get hasStockThresholdBeenEdited(): boolean {
    return this.formStockThreshold?.dirty ?? false;
  }

  get hasStockThresholdErrors(): {
    errors: ValidationErrors; error: string; field: string;
  }[] {
    const errors: { errors: ValidationErrors; error: string; field: string; }[] = [];
    const pctErrors = this.formStockThreshold?.controls['pct']?.errors;
    const negPctErrors = this.formStockThreshold?.controls['negPct']?.errors;

    if (pctErrors) {
      errors.push({
        errors: pctErrors,
        error: getFieldErrorString(pctErrors, 'pct'),
        field: 'pct',
      });
    }
    if (negPctErrors) {
      errors.push({
        errors: negPctErrors,
        error: getFieldErrorString(negPctErrors, 'negPct'),
        field: 'negPct',
      });
    }
    return errors;
  }

  get isStockThresholdSubmitDisabled(): boolean {
    return (this.canStockThresholdBeEdited === false) || (this.canStockThresholdBeEdited === true && this.hasStockThresholdBeenEdited === false) || (this.formStockThreshold?.invalid ?? true);
  }

  get canStockThresholdBeEdited(): boolean {
    const access = this.stockManagerUserAccess?.stockThreshold;
    return access?.canEdit ?? false;
  }

  get canStockThresholdBeSubmitted(): boolean {
    const access = this.stockManagerUserAccess?.stockThreshold;
    return access?.canSubmit ?? false;
  }

  get canStockThresholdBeViewed(): boolean {
    const access = this.stockManagerUserAccess?.stockThreshold;
    return access?.canView ?? false;
  }

  //#endregion

  //#region ACCESSOR - USER SETTINGS + USER ACCESS + STORE
  get selectedStore(): IStore {
    return this._selectedStore();
  }

  get userSettings(): IUser {
    return this._user();
  }

  get stockManagerUserAccess(): IUserAccessFeatureStockRules {
    return this._uaStockRules();
  }

  //#endregion

  //#region ACCESSOR - GLOBALS
  get haveAnyEditsBeenMade(): boolean {
    return (this.formStockThreshold?.dirty ?? false);
  }

  get stockThreshold(): IStockItemEditThresholds {
    return this._stockThreshold();
  }

  //#endregion

  //#region LIFECYCLE HOOKS
  ngOnInit(): void {
    this.formStockThreshold = this.formBuilder.group({});
  }

  //#endregion

  //#region FORM - CLICK HANDLERS
  onRevertForm(form: string): void {
    switch (form) {
      case 'all':
        this.revertStockThresholdDetails();
        break;
      case 'stockThreshold':
        this.revertStockThresholdDetails();
        break;
    }
  }

  onSubmitForm(form: string): void {
    let data: Partial<IStockItemEditThresholds> = {...this.stockThreshold};
    switch (form) {
      case 'all':
        data = {
          ...data,
          ...this.generateStockThresholdPatch(data),
        };
        break;
      case 'stockThreshold':
        if (!this.canStockThresholdBeSubmitted) break;
        data = {...data, ...this.generateStockThresholdPatch(data)};
        break;
      default:
        break;
    }
    this._store.dispatch(settingStoreStockEditThresholdRules.setStockEditThresholdRules({rules: data as IStockItemEditThresholds}));
    this.formStockThreshold.markAsPristine();
  }

  //#endregion

  //#region FORM - BUILD FORMS FROM ACCESS

  private initializeForms(access: IUserAccessFeatureStockRules, threshold: IStockItemEditThresholds): void {
    this.buildStockThresholdForm(access?.stockThreshold, threshold);
  }

  private buildStockThresholdForm(access: ICrudOptions, threshold: IStockItemEditThresholds): void {
    if (access?.canView && this.formStockThreshold.pristine) {
      const {sellPriIncl1} = threshold;
      const tempForm = this.formBuilder.group({});

      // Convert decimal to percentage for display (0.1 -> 10, 0.23 -> 23)
      const pctDisplay = (sellPriIncl1?.pct ?? 0) * 100;
      const negPctDisplay = (sellPriIncl1?.negPct ?? 0) * 100;

      this.addFormControl(tempForm, 'pct', pctDisplay, false, [Validators.min(0), Validators.max(100)]);
      this.addFormControl(tempForm, 'negPct', negPctDisplay, false, [Validators.min(0), Validators.max(100)]);

      tempForm.markAsPristine();
      this.formStockThreshold = tempForm;
    }
  }

  private addFormControl<T = unknown>(form: FormGroup, controlName: string, value: T, disabled: boolean, validators: ValidatorFn[] = []): void {
    form.addControl(controlName, this.formBuilder.control({value, disabled}, validators));
  }

  //#endregion

  //#region FORM - REVERT FORM EDITS
  private revertStockThresholdDetails(): void {
    const threshold = this.stockThreshold;
    const {sellPriIncl1} = threshold;

    // Convert decimal to percentage for display (0.1 -> 10, 0.23 -> 23)
    const pctDisplay = (sellPriIncl1?.pct ?? 0) * 100;
    const negPctDisplay = (sellPriIncl1?.negPct ?? 0) * 100;

    this.formStockThreshold.reset({
      pct: pctDisplay,
      negPct: negPctDisplay,
    });
  }

  //#endregion

  //#region FORM - GENERATE PATCH DATA

  private generateStockThresholdPatch(data: Partial<IStockItemEditThresholds>): Partial<IStockItemEditThresholds> {
    const formValues = this.formStockThreshold.getRawValue();
    const currentPct = this.stockThreshold?.sellPriIncl1?.pct;
    const currentNegPct = this.stockThreshold?.sellPriIncl1?.negPct;

    // Convert percentage to decimal for saving (10 -> 0.1, 23 -> 0.23)
    const nextPct = (formValues?.pct ?? 0) / 100;
    const nextNegPct = (formValues?.negPct ?? 0) / 100;

    const thresholdPatch: Partial<IStockItemEditThresholds> = {};

    if (currentPct !== nextPct || currentNegPct !== nextNegPct) {
      thresholdPatch.sellPriIncl1 = {
        pct: nextPct,
        negPct: nextNegPct,
      };
    }

    if (!(Object.keys(thresholdPatch).length > 0)) return data;
    data = {...data, ...thresholdPatch};
    return data;
  }

  //#endregion

  //#region MODAL - DISCARD UNSAVED CHANGES
  private async onShowModalUnsavedChanges(ref: SettingsReferenceEnum): Promise<void> {
    const modalProps: ISharedModalBasic = {
      buttonAccept: true,
      buttonAcceptText: 'Discard Changes & Continue',
      buttonClose: true,
      buttonCloseCross: false,
      buttonCloseText: 'Cancel',
      modalTitle: 'Unsaved Changes',
      modalTitleIcon: IconsEnum.infoSlabCircleOutline,
      modalTitleIconColor: IconColourEnum.yellow,
      contextHeading: 'If you leave this page, your changes will be lost.',
      contextDescription: ['Are you sure you want to continue?'],
      cssClass: ['shared-basic-modal-css-small'],
    };
    await this.modalUtils.createModal(modalProps).then((result: IModalDismissResult) => {
      this._store.dispatch(settingsSetSelected.setSelectedReference({ref: SettingsReferenceEnum.accountUserProfile}));
      if (result.role === 'modal-accept') {
        this._store.dispatch(settingsSetSelected.setSelectedReferenceForNavigation({ref}));
      }
    });
  }

  //#endregion

}
